/**
 * Adapter configuration as stored in ioBroker native config
 */
export interface AdapterConfig {
  /** Beszel Hub URL, e.g. http://192.168.1.100:8090 */
  url: string;
  /** Login username */
  username: string;
  /** Login password */
  password: string;
  /** Poll interval in seconds */
  pollInterval: number;
  /**
   * v0.4.3 (B5): per-request HTTP timeout in seconds. Defaults to 15s when
   * missing/unparseable. Useful when the Hub returns large container/stats
   * payloads or sits behind a slow link.
   */
  requestTimeout?: number;

  // Metric toggles
  /** Enable uptime states */
  metrics_uptime: boolean;
  /** Enable agent version state */
  metrics_agentVersion: boolean;
  /** Enable systemd services states */
  metrics_services: boolean;

  /** Enable CPU usage state */
  metrics_cpu: boolean;
  /** Enable load average states */
  metrics_loadAvg: boolean;
  /** Enable CPU breakdown states (user/system/iowait/steal/idle) */
  metrics_cpuBreakdown: boolean;

  /** Enable memory states */
  metrics_memory: boolean;
  /** Enable memory detail states (buffers, ZFS ARC) */
  metrics_memoryDetails: boolean;
  /** Enable swap states */
  metrics_swap: boolean;

  /** Enable disk usage states */
  metrics_disk: boolean;
  /** Enable disk speed states */
  metrics_diskSpeed: boolean;
  /** Enable extra filesystem states */
  metrics_extraFs: boolean;

  /** Enable network states */
  metrics_network: boolean;

  /** Enable temperature state (avg top 3 sensors) */
  metrics_temperature: boolean;
  /** Enable per-sensor temperature states */
  metrics_temperatureDetails: boolean;

  /** Enable GPU states */
  metrics_gpu: boolean;

  /** Enable container states */
  metrics_containers: boolean;

  /** Enable battery states */
  metrics_battery: boolean;

  // --- v0.6.0 additions (all default off — opt-in detail/peaks) ---
  // v0.16.0: required like every other toggle. They were optional only because they
  // arrived later; the manifest ships all of them with a default, `effectiveConfig`
  // writes to them, and one interface with two rules for the same kind of field was a
  // trap waiting for the next addition.
  /** Per-core CPU usage states */
  metrics_cpuCores: boolean;
  /** Peak CPU usage state */
  metrics_cpuPeak: boolean;
  /** Peak memory state */
  metrics_memoryPeak: boolean;
  /** Disk I/O detail (bytes + utilization + wait times) */
  metrics_diskIo: boolean;
  /** Peak disk read/write speed states */
  metrics_diskPeak: boolean;
  /** Per-network-interface states */
  metrics_networkInterfaces: boolean;
  /** Peak network sent/received states */
  metrics_networkPeak: boolean;
  /** GPU detail states (package power + per-engine usage) */
  metrics_gpuDetails: boolean;
  // --- v0.11.0 additions (Beszel 0.18.8) ---
  /** Per-fan RPM states (Beszel 0.18.8+, Linux hwmon) */
  metrics_fans: boolean;
  // --- v0.15.0 additions (Beszel 0.19.0) ---
  /** Per-pool ZFS states: usage, throughput, health (Beszel 0.19.0+) */
  metrics_zfs: boolean;
  /** v0.17.0: ZFS pool DETAILS from the `zfs_pools` collection (scrub, vdevs, datasets). */
  metrics_zfsDetails: boolean;
  /** v0.17.0: SMART devices from the `smart_devices` collection. */
  metrics_smart: boolean;
  /** v0.17.0: per-unit systemd detail from the `systemd_services` collection. */
  metrics_servicesDetails: boolean;
}

/**
 * System info object from Beszel systems record
 */
export interface SystemInfo {
  /** Uptime in seconds */
  u?: number;
  /** Agent version */
  v?: string;
  /** Systemd services [total, failed] */
  sv?: [number, number];
  /** Load average [1m, 5m, 15m] */
  la?: [number, number, number];
  /** Battery [percent, charge_state] */
  bat?: [number, number];
  /** Custom root disk name set on the agent (`FILESYSTEM=device__name`), Beszel 0.19.0+ */
  rdn?: string;
}

/**
 * Static hardware / OS info for one system. Lives in the `system_details`
 * collection (Beszel v0.18.0+); the adapter fetches it only when the
 * "System info" metric is enabled, and rarely (once at start + when a new
 * system appears) because it changes only on agent restart/upgrade.
 *
 * Every field is optional — an older Beszel without the collection, or a
 * partially-populated agent, simply yields absent fields (→ no state created).
 * Column names verified against the beszel v0.18.7 collection snapshot (the
 * bundled release, not main) — note `os_name` is snake_case and `os` is a
 * numeric platform enum, not a string.
 */
export interface SystemDetails {
  /** Host name */
  hostname?: string;
  /** OS platform enum: 0=Linux, 1=Darwin (macOS), 2=Windows, 3=FreeBSD */
  os?: number;
  /** Full OS name, e.g. "Ubuntu 22.04" / "macOS 14.1" */
  os_name?: string;
  /** Kernel version */
  kernel?: string;
  /** CPU model name */
  cpu?: string;
  /** CPU architecture, e.g. "x86_64" / "arm64" */
  arch?: string;
  /** Physical CPU cores */
  cores?: number;
  /** Logical CPU threads */
  threads?: number;
  /** Container engine is Podman (vs Docker) */
  podman?: boolean;
}

/**
 * A system record from /api/collections/systems/records
 */
export interface BeszelSystem {
  /** PocketBase record ID */
  id: string;
  /** Display name */
  name: string;
  /** Current system status */
  status: "up" | "down" | "paused" | "pending";
  /** Hostname or IP */
  host: string;
  /** System info object */
  info: SystemInfo;
  /**
   * Static hardware/OS info, attached by the poll loop from the separately
   * fetched `system_details` collection (only when "System info" is enabled).
   * Absent on systems whose details aren't (yet) cached.
   */
  details?: SystemDetails;
}

/**
 * A system_details record from /api/collections/system_details/records.
 */
export interface BeszelSystemDetailsRecord {
  /** Reference to systems.id */
  system: string;
  /** Static hardware/OS fields */
  details: SystemDetails;
}

/**
 * Extra filesystem stats
 */
export interface FsStats {
  /** disk total GB */
  d?: number;
  /** disk used GB */
  du?: number;
  /** read MB/s */
  r?: number;
  /** write MB/s */
  w?: number;
  /** cumulative device read bytes (Beszel 0.19.0+, `omitzero`) */
  tr?: number;
  /** cumulative device write bytes (Beszel 0.19.0+, `omitzero`) */
  tw?: number;
}

/**
 * Per-pool ZFS metrics of one collection interval (Beszel 0.19.0+, `system_stats.stats.z`).
 * Verified against beszel v0.19.0 `internal/entities/system/system.go` (`ZfsPool`): capacities in
 * GiB like the root disk, throughput in bytes/s (`omitzero` — absent when idle), health as the
 * zpool word (ONLINE, DEGRADED, FAULTED, …).
 */
export interface ZfsPoolStats {
  /** total capacity GiB */
  d?: number;
  /** allocated GiB */
  du?: number;
  /** read throughput bytes/s */
  rb?: number;
  /** write throughput bytes/s */
  wb?: number;
  /** pool health word */
  h?: string;
}

/**
 * GPU data
 */
export interface GPUData {
  /** GPU name */
  n?: string;
  /** GPU usage % */
  u?: number;
  /** GPU memory used MB (agent reports MiB/bytes → MB, verified gpu.go) */
  mu?: number;
  /** GPU memory total MB (agent reports MiB/bytes → MB, verified gpu.go) */
  mt?: number;
  /** GPU power W */
  p?: number;
  /** Package power W (v0.18.7) */
  pp?: number;
  /** Per-engine usage %: engine name -> % (v0.18.7) */
  e?: Record<string, number>;
}

/**
 * The stats object inside a system_stats record
 */
export interface SystemStats {
  /** CPU usage % */
  cpu?: number;
  /** RAM used GB */
  mu?: number;
  /** RAM total GB */
  m?: number;
  /** RAM % */
  mp?: number;
  /** Buffers + cache GB */
  mb?: number;
  /** ZFS ARC GB */
  mz?: number;
  /** Swap used GB */
  su?: number;
  /** Swap total GB */
  s?: number;
  /** Disk used GB */
  du?: number;
  /** Disk total GB */
  d?: number;
  /** Disk % */
  dp?: number;
  /** Disk read MB/s */
  dr?: number;
  /** Disk write MB/s */
  dw?: number;
  /** Network sent MB/s */
  ns?: number;
  /** Network recv MB/s */
  nr?: number;
  /** Temperatures map sensor->°C */
  t?: Record<string, number>;
  /** Load avg [1m, 5m, 15m] */
  la?: [number, number, number];
  /** GPU data */
  g?: Record<string, GPUData>;
  /** Extra filesystems */
  efs?: Record<string, FsStats>;
  /** Battery [%, charge_state] */
  bat?: [number, number];
  /** CPU breakdown [user, sys, iowait, steal, idle] % */
  cpub?: number[];
  // --- v0.18.7 additions (all optional → absent on older Beszel versions) ---
  /** Peak CPU usage % in the interval */
  cpum?: number;
  /** Peak RAM used GB */
  mm?: number;
  /** Peak disk read MB/s */
  drm?: number;
  /** Peak disk write MB/s */
  dwm?: number;
  /** Peak network sent MB/s */
  nsm?: number;
  /** Peak network received MB/s */
  nrm?: number;
  /**
   * Per-interface bandwidth: name -> [up bytes/s, down bytes/s, total up bytes,
   * total down bytes]. up/down are rates; total_up/total_down are cumulative
   * since boot (the only genuine cumulative network counters Beszel exposes).
   */
  ni?: Record<string, [number, number, number, number]>;
  /** Per-core CPU busy % */
  cpus?: number[];
  /** Disk I/O stats [read time %, write time %, io util %, r_await ms, w_await ms, weighted io %] */
  dios?: number[];
  // --- v0.18.8 additions (both absent on older Beszel versions) ---
  /**
   * Fan speeds: fan name -> RPM. Linux hwmon only; keys are
   * `<chip>_<label-or-fanN>` and may contain spaces. 0 RPM is a real reading
   * (stopped fan) — verified against beszel v0.18.8 agent/fans.go.
   */
  f?: Record<string, number>;
  /**
   * Per-battery charge: battery name -> percent (0–100). The aggregate `bat`
   * tuple (primary battery) stays alongside. Names are OS-reported with a
   * `Battery N` fallback — verified against beszel v0.18.8 agent/system.go.
   */
  bats?: Record<string, number>;
  // --- v0.19.0 additions (both absent on older Beszel versions) ---
  /** ZFS pools: pool name -> metrics (Beszel 0.19.0+). */
  z?: Record<string, ZfsPoolStats>;
  /** Cumulative device counters [read bytes, write bytes] since boot (Beszel 0.19.0+, `omitzero`). */
  diot?: [number, number];
}

// Note: `b`/`bm` (Bandwidth) and `dio`/`diom` (DiskIO) are deliberately NOT in
// this interface. Verified against beszel v0.18.7 agent/network.go:231 +
// agent/disk.go:638-641: they are byte/s *rates*, identical to `ns`/`nr` and
// `dr`/`dw` in different units — redundant, so not surfaced. `diosm` (peak
// dios) has no consumer either.

/**
 * A system_stats record from /api/collections/system_stats/records
 */
export interface BeszelSystemStats {
  /** Reference to systems.id — the only reference the client keys on. */
  system: string;
  /** Metric values */
  stats: SystemStats;
}

/**
 * A container record from /api/collections/containers/records
 */
export interface BeszelContainer {
  /** PocketBase record ID */
  id: string;
  /** Reference to systems.id */
  system: string;
  /** Container name */
  name: string;
  /** running / exited / etc. */
  status: string;
  /** 0=none 1=starting 2=healthy 3=unhealthy */
  health: number;
  /** CPU usage % */
  cpu: number;
  /** Memory usage MB */
  memory: number;
  /** Docker image name */
  image: string;
  /**
   * Combined network throughput in bytes/s (sent + recv). Hub stores
   * `Bandwidth[0] + Bandwidth[1]` in the `net` column (v0.18.7). Optional —
   * absent on an older Hub → no container network state.
   */
  net?: number;
}

/**
 * PocketBase list response — only the two fields the client actually reads.
 * `page`/`perPage`/`totalItems` are dropped from the raw body during coercion
 * (fetchAllPages drives pagination from `totalPages` alone).
 */
export interface PocketBaseList<T> {
  /** Total number of pages */
  totalPages: number;
  /** Records on this page that survived coercion */
  items: T[];
  /**
   * Records the page carried before coercion dropped any. Distinguishes the
   * genuinely empty page (past the last one) from a page whose records were all
   * unusable — only the former ends the pagination walk.
   */
  rawCount: number;
}

/**
 * PocketBase auth response
 */
export interface AuthResponse {
  /** Auth token — sent as the bare `Authorization` header value (Beszel/PocketBase uses no "Bearer " prefix). */
  token: string;
}

/**
 * Error codes the BeszelClient tags onto thrown errors via `err.code`. main.ts's
 * `classifyError` reads exactly these (plus Node's own network codes like
 * ENOTFOUND). Shared so the producer (client) and consumer (main) can't drift on
 * a bare string literal — a typo is now a compile error (F4). The timeout path
 * uses ETIMEDOUT so classification no longer depends on a message substring (N6).
 */
export type BeszelErrorCode =
  "UNAUTHORIZED" | "FORBIDDEN" | "RATE_LIMITED" | "HTTP_ERROR" | "INVALID_AUTH_RESPONSE" | "ETIMEDOUT";

/**
 * One row of the Hub's `zfs_pools` collection — the DETAIL record the agent refreshes
 * hourly, next to the summary the adapter already reads from `stats.z`.
 *
 * Verified against the bundled Beszel 0.19.0 source: the hub writes `name`, `health`,
 * `size`/`alloc`/`free` (bytes) plus the three JSON columns from
 * `internal/hub/systems/system_zfs.go:upsertZfsPoolRecord`. Read access is the same
 * `systemScopedReadRule` as `system_stats` (`internal/hub/collections.go`), so the
 * adapter's existing credentials suffice.
 */
export interface ZfsPoolDetail {
  /** PocketBase record ID */
  id: string;
  /** Reference to systems.id */
  system: string;
  /** Pool name as `zpool` reports it */
  name: string;
  /** Scrub/resilver state: NONE | SCANNING | FINISHED | CANCELED (absent → no scrub info) */
  scrubState?: string;
  /** Free-text progress the agent read from `zpool status` (e.g. "12.3% done") */
  scrubProgress?: string;
  /** Errors the last scrub found */
  scrubErrors?: number;
  /** vdevs of the pool, each with its own error counters */
  vdevs: ZfsVdev[];
  /** datasets of the pool */
  datasets: ZfsDataset[];
}

/** One vdev of a ZFS pool (mirror, raidz or a leaf disk) with its error counters. */
export interface ZfsVdev {
  /** vdev name as `zpool status` prints it (`mirror-0`, `sda`) */
  name: string;
  /** ONLINE | DEGRADED | FAULTED | … — same vocabulary as the pool health */
  state?: string;
  /** Read errors counted since the pool was last cleared */
  readErrors: number;
  /** Write errors counted since the pool was last cleared */
  writeErrors: number;
  /** Checksum errors counted since the pool was last cleared */
  checksumErrors: number;
}

/** One ZFS dataset with its usage. Bytes as the hub stores them. */
export interface ZfsDataset {
  /** Dataset name (`tank/media`) */
  name: string;
  /** Used bytes */
  used?: number;
  /** Available bytes */
  avail?: number;
  /** Mount point, absent for a dataset that is not mounted */
  mountpoint?: string;
}

/**
 * One row of the Hub's `smart_devices` collection. Verified against the bundled 0.19.0
 * schema (`internal/migrations/0_collections_snapshot_0_19_0.go`): `name`, `model`,
 * `state`, `capacity`, `temp`, `firmware`, `serial`, `type`, `hours`, `cycles`.
 * `attributes` (the raw SMART attribute table) is deliberately NOT read — it is a
 * vendor-specific blob whose keys differ per device, and an adapter cannot name
 * datapoints it cannot describe.
 */
export interface SmartDevice {
  /** PocketBase record ID */
  id: string;
  /** Reference to systems.id */
  system: string;
  /** Device node as smartctl names it (`/dev/sda`, `nvme0`) */
  name: string;
  /** PASSED | FAILED — the overall SMART verdict */
  state?: string;
  /** Device model as smartctl reports it */
  model?: string;
  /** Serial number */
  serial?: string;
  /** Firmware revision */
  firmware?: string;
  /** Transport as smartctl reports it (`sat`, `nvme`, …) */
  type?: string;
  /** Device temperature in °C */
  temperature?: number;
  /** Capacity in bytes */
  capacity?: number;
  /** Power-on hours */
  hours?: number;
  /** Power cycle count */
  cycles?: number;
}

/**
 * One row of the Hub's `systemd_services` collection. The hub INSERTs all columns of a
 * batch in one statement (`internal/hub/systems/system.go:337`), so a row never carries
 * a missing `state`/`cpu`/`memory`. Only the `list` rule is granted for this collection
 * — the adapter never fetches a single record, it pages the list like every other one.
 */
export interface SystemdService {
  /** PocketBase record ID */
  id: string;
  /** Reference to systems.id */
  system: string;
  /** Unit name, e.g. `ssh.service` */
  name: string;
  /** 0 active · 1 inactive · 2 failed · 3 activating · 4 deactivating · 5 reloading */
  state: number;
  /** 0 dead · 1 running · 2 exited · 3 failed · 4 unknown */
  sub: number;
  /** CPU usage in percent */
  cpu: number;
  /** Peak CPU usage in percent within the sample window */
  cpuPeak: number;
  /** Resident memory in bytes */
  memory: number;
  /** Peak resident memory in bytes */
  memPeak: number;
}
